import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { DateConstants } from '../core/persian-date';
import { Constants } from '../states/constants';
import { JobCriteria, KarpashaProgressStatus, PaymentProvider, UserCriteria } from '../common';
import { Utility } from '../core/common';

@Injectable({
  providedIn: 'root'
})
export class KarpashaService {
  agentRegisterationPrice = 95000;

  constructor(private http: HttpClient, private router: Router) { }

  static seekerJobCompareFields = [
    { title: 'شغل', compareKey: undefined, seeker: undefined, job: Constants.Job.jobFields.title },
    { title: 'عنوان‌های شغلی', compareKey: 'jobtitle', seeker: Constants.User.seekerFields.jobTitles, job: Constants.Job.jobFields.jobTitle },
    { title: 'متن آگهی', compareKey: 'description', seeker: undefined, job: Constants.Job.jobFields.description },
    { title: 'موقعیت', compareKey: 'location', seeker: Constants.User.seekerFields.location, job: Constants.Job.jobFields.location },
    { title: 'نوع قرارداد', compareKey: 'jobtype', seeker: Constants.User.seekerFields.jobtype, job: Constants.Job.jobFields.jobType },
    { title: 'ساعت کاری', compareKey: 'workHours', seeker: Constants.User.seekerFields.workHours, job: Constants.Job.jobFields.workHours },
    { title: 'مهارت', compareKey: 'skills', seeker: Constants.User.seekerFields.skills, job: Constants.Job.jobFields.skills },
    { title: 'جنسیت', compareKey: 'gender', seeker: Constants.User.seekerFields.gender, job: Constants.Job.jobFields.gender },
    { title: 'سن', compareKey: 'age', seeker: Constants.User.seekerFields.age, job: Constants.Job.jobFields.age },
    { title: 'نظام‌وظیفه', compareKey: 'military', seeker: Constants.User.seekerFields.military, job: Constants.Job.jobFields.military },
    { title: 'مدرک تحصیلی', compareKey: 'degree', seeker: Constants.User.seekerFields.degree, job: Constants.Job.jobFields.degree },
    { title: 'سابقه کار', compareKey: 'experience', seeker: Constants.User.seekerFields.experience, job: Constants.Job.jobFields.experience },
    { title: 'تاهل', compareKey: 'marital', seeker: Constants.User.seekerFields.marital, job: Constants.Job.jobFields.marital },
    { title: 'دستمزد', compareKey: 'salary', seeker: Constants.User.seekerFields.salary, job: Constants.Job.jobFields.salary },
    { title: 'بیمه', compareKey: 'insurance', seeker: Constants.User.seekerFields.insurance, job: Constants.Job.jobFields.insurance },
    { title: 'اطلاعات تکمیلی', compareKey: 'notes', seeker: Constants.User.seekerFields.karpashaNotes, job: Constants.Job.jobFields.karpashaInfo },
    { title: 'رانندگی', compareKey: 'driving', seeker: Constants.User.seekerFields.driving, job: Constants.Job.jobFields.driving },
    { title: 'گواهینامه‌ها', compareKey: 'certificates', seeker: Constants.User.seekerFields.certificates, job: Constants.Job.jobFields.certificates },
    { title: 'مهارت‌های زبانی', compareKey: 'language', seeker: Constants.User.seekerFields.language, job: Constants.Job.jobFields.language },
    { title: 'آزمون‌ها', compareKey: 'exams', seeker: Constants.User.seekerFields.exams, job: undefined },
  ];
  static PipelineStages = [
    { index: 0, key: "unknown", title: "تعیین تکلیف", hideable: true, color: 'red' },
    { index: 1, key: "seeker", title: "درخواست کارجو", hideable: true, color: '#034b8e' },
    { index: 2, key: "acceptable", title: "بررسی اولیه", color: '#EE8200' },
    { index: 3, key: "negotiate", title: "مذاکره", color: '#317bf2', description: "با کارفرما جهت تایید شرایط و معرفی کارجو تماس گرفته شود" },
    { index: 4, key: "interview", title: "انجام مصاحبه", color: '#3590A4', description: "قرار مصاحبه گذاشته شود", criteria: 'score-100' },
    { index: 5, key: "contract", title: "عقد قرارداد", color: '#008a5f', description: "عقد قرارداد پیگیری شود", criteria: 'score-100' },
    { index: 6, key: "payment", title: "اخذ تعرفه", description: "پیگیری اشتغال تا اخذ تعرفه", color: '#8f9faa', criteria: 'score-100' }
  ];
  static getPipelineStageTitle(key: string, unknown = true) {
    return KarpashaService.PipelineStages.find(s => s.key === key)?.title || (unknown ? KarpashaService.PipelineStages[0]?.title : key);
  }
  static getPipelineStageColor(key: string) { return KarpashaService.PipelineStages.find(s => s.key === key)?.color; }
  static getStatusTitle(status: KarpashaProgressStatus | string) {
    switch (isNaN(+status) ? KarpashaProgressStatus[status] : status) {
      case KarpashaProgressStatus.InProgress: return 'در حال پیگیری';
      case KarpashaProgressStatus.Rejected: return 'کنسل شده';
      case KarpashaProgressStatus.Muted: return 'معلق شده';
      case KarpashaProgressStatus.Completed: return 'تکمیل شده';
      default: return null;
    }
  }

  private static minimumWage = 5.6 * 1000000;
  static Comparer: {
    [key: string]: ((seeker: any, job: any) => boolean) | any
  } = {
      _score(seeker: any, job: any, clearance: any) {
        var score = 0;
        var keys = Object.getOwnPropertyNames(KarpashaService.Comparer).filter(_ => !_.startsWith('_'));
        keys.forEach(key =>
          score += (clearance?.[key] ? clearance[key].cleared : KarpashaService.Comparer[key](seeker, job)) != false ? 1 : 0
        );
        return score / keys.length;
      },

      jobtitle: (seeker, job) => seeker?.Seeker?.JobAlertSettings?.JobTitles?.
        some(_ => [job?.Title, ...(job?._metaJobTitleTags?.map(jt => jt.Title) || [])].indexOf(_) >= 0),
      location: (seeker, job) => seeker?.Seeker?.State === job?.State && seeker?.Seeker?.City === job?.City ?
        (seeker?.Seeker?.District === job?.District || undefined) : false,
      age: (seeker, job) => {
        var age = (Date.now() - new Date(seeker?.Seeker?.Birthday).getTime()) / DateConstants.oneYear;
        return (!job?.MinAge && !job?.MaxAge) || ((!job?.MinAge || age >= job?.MinAge) && (!job?.MaxAge || age <= job?.MaxAge));
      },
      salary: (seeker, job) => {
        var min = +job?.MinSalary > 0 ? +job?.MinSalary : KarpashaService.minimumWage;
        var max = +job?.MaxSalary > 0 ? +job?.MaxSalary : KarpashaService.minimumWage;
        var salary = seeker?.Seeker?.JobAlertSettings?.MinSalary > 0 ? +seeker?.Seeker?.JobAlertSettings?.MinSalary : KarpashaService.minimumWage;
        return min <= salary && salary <= max;
      },
      insurance: (seeker, job) => (!seeker?.Seeker?.JobAlertSettings?.NeedInsurance || job?.HasInsurance),
      gender: (seeker, job) => job?.Gender == -1 || seeker?.Seeker?.Gender == job?.Gender,
      marital: (seeker, job) => job?.MaritalStatus == -1 || seeker?.Seeker?.MaritalStatus == job?.MaritalStatus,
      military: (seeker, job) => job?.Gender != 0 || !job?.MilitaryExcludeNotServed || [3, 4, 5, 6].includes(seeker?.Seeker?.MilitaryStatus),
      jobtype: (seeker, job) => !seeker?.Seeker?.JobAlertSettings?.JobTypes?.length || seeker?.Seeker?.JobAlertSettings?.JobTypes?.some(_ => job?.JobTypes?.indexOf(_) >= 0),
      skills: (seeker, job) => job?.RequiredSkills?.every(js => seeker?.Seeker?.Skills?.find(ss => ss.Title == js)),
      notes: (seeker, job) => undefined,
      // workhours: new SeekerJobComparerItem('ساعات کاری', 'WorkHours', 'WorkHours'),
      // education: new SeekerJobComparerItem('تحصیلات', 'Education', 'Education'),
      // experience: new SeekerJobComparerItem('سابقه کار', 'Experience', 'Experience'),
    };

  getSeekerJobReport(karpashaId: string, seekerId: string, statuses: KarpashaProgressStatus[]) {
    return this.http.post<any>(`${environment.app.apiUrl}karpasha/report/seeker-job`,
      { karpashaId, seekerId, statuses }).toPromise();
  }

  getActivityReport(karpashaId?: string, seekerId?: string) {
    return this.http.post<any>(`${environment.app.apiUrl}karpasha/report/activity`,
      { karpashaId, seekerId }).toPromise();
  }

  getProgressReport(karpashaId?: string, seekerId?: string) {
    return this.http.post<any>(`${environment.app.apiUrl}karpasha/report/progress`,
      { karpashaId, seekerId }).toPromise();
  }

  searchJobs(seekerId: string, criteria: JobCriteria, from = 0, limit = 10, addLog = false) {
    return this.http.post<{ total: number, jobs: any[] }>(environment.app.apiUrl +
      `karpasha/job-search/${seekerId}/${from}/${limit}/${addLog}`, criteria).toPromise();
  }

  getUsers(karpashaId?: string, status = KarpashaProgressStatus.InProgress) {
    return this.http.post<{ users: any[], data: { [key: string]: any } }>(
      `${environment.app.apiUrl}karpasha/users`, Utility.removeEmpty({ karpashaId, status })).toPromise();
  }
  getSeeker(seekerId: string) {
    return this.http.get<{ seeker: any, data: any }>(
      `${environment.app.apiUrl}karpasha/seeker/${seekerId}`).toPromise();
  }

  getEmployer(employerOrJobId: string, allJobs = false) {
    return this.http.post<{ employer: any, jobs: any[] }>(
      `${environment.app.apiUrl}karpasha/employer/jobs`, { employerOrJobId, allJobs }).toPromise();
  }

  getSeekerJobs(seekerId: string, jobId: string, statuses: KarpashaProgressStatus[]) {
    return this.http.post<{ list: any[], data: { [key: string]: any } }>(
      `${environment.app.apiUrl}karpasha/list`, Utility.removeEmpty({ seekerId, jobId, statuses })).toPromise();
  }

  getMySeekerJobs(criteria?: any, limit = 100) {
    return this.http.post<{ list: any[], total: number }>(environment.app.apiUrl + `karpasha/seeker-job/mine/`, criteria || {}, { params: { limit } }).toPromise();
  }
  saveMySeekerJob(seekerJob: any) {
    return this.http.post<any>(environment.app.apiUrl + 'karpasha/seeker-job/mine/save', seekerJob).toPromise();
  }
  getMatchedJobsForSeeker(seekerId: string, approved?: boolean) {
    return this.http.post<{ proposed: any[], approved: any[], rejected: [] }>(environment.app.apiUrl + `karpasha/matched-jobs`, { seekerId, approved }).toPromise();
  }

  getSeekerJob(id: string) {
    return this.http.get<any>(environment.app.apiUrl + 'karpasha/seeker-job/' + id).toPromise();
  }

  getGuestSeekerJob(id: string) {
    return this.http.get<any>(environment.app.apiUrl + 'karpasha/seeker-job/' + id + '/guest').toPromise();
  }

  saveSeekerJob(seekerJob: any) {
    return this.http.post<any>(environment.app.apiUrl + 'karpasha/seeker-job', seekerJob).toPromise();
  }

  setKarpasha(type: 'seeker' | 'employer', userId: string, karpashaId: string) {
    return this.http.post<any>(environment.app.apiUrl + `karpasha/set-karpasha`, { type, userId, karpashaId }).toPromise();
  }
  setGuide(type: 'seeker' | 'employer', userId: string, guideId: string) {
    return this.http.post<any>(environment.app.apiUrl + `karpasha/set-guide`, { type, userId, guideId }).toPromise();
  }

  saveProgress(type: 'seeker' | 'employer', userId: string, karpashaProgress: any) {
    return this.http.post<any>(environment.app.apiUrl + `karpasha/set-progress/${userId}/${type}`, karpashaProgress).toPromise();
  }

  searchUser(criteria: UserCriteria, jobId?: string, from = 0, limit = 30) {
    return this.http.post<{ total: number; users: any[] }>(environment.app.apiUrl + `karpasha/search-user`, { criteria, jobId, from, limit })
      .toPromise();
  }
  searchJob(criteria: JobCriteria, from = 0, limit = 30) {
    return this.http.post<{ total: number; jobs: any[] }>(environment.app.apiUrl + `karpasha/search-job`, { criteria, from, limit })
      .toPromise();
  }
  saveKarbaladRequest(employerId: string, employer: any, job: any) {
    return this.http.post<any>(environment.app.apiUrl + `karpasha/employer/save-karbalad/${employerId}`, { employer, job }).toPromise();
  }

  agreement(key: string, payload: any, updateId?: string, preview?: boolean, ...announce: string[]) {
    return this.http.post<any>(environment.app.apiUrl + `karpasha/agreement/${key}`,
      payload, { params: Utility.removeEmpty({ updateId, preview, announce }) }).toPromise();
  }

  async paySeekerJobPayment(seekerJobId: string, manualToken?: string) {
    const response: any = await this.http.get(environment.app.apiUrl + `karpasha/seeker-job/pay/${seekerJobId}`,
      { params: Utility.removeUndefined({ 'rb': location.href, 'manual': manualToken || undefined }), headers: { 'x-kp-alive': 'true' } }
    ).toPromise();

    if (response.provider == PaymentProvider.Manual)
      this.router.navigateByUrl(`/seeker/karpasha?pid=${response.id}`)
    // .then(() => location.reload());
    else location.href = response.link;
  }
  async makeSeekerJobPayment(seekerJobId: string) {
    return this.http.get<any>(environment.app.apiUrl + `karpasha/seeker-job/make-payment/${seekerJobId}`,
      { params: { 'rb': location.href }, headers: { 'x-kp-alive': 'true' } }
    ).toPromise();
  }

  attendancy(start?: boolean, info?: { [key: string]: string }) {
    return this.http.post<any>(environment.app.apiUrl + `karpasha/attendancy` + (start == undefined ? '' : ('/' + start)), info).toPromise();
  }

  async getLeadStatsForAllStates(query: any) {
    return this.http.post<any>(environment.app.apiUrl + `karpasha/lead-stats`, query).toPromise();
  }

  confirmRecruitLead(userId: any) {
    return this.http.post<any>(environment.app.apiUrl + `karpasha/confirm-recruit-lead/${userId}`, null).toPromise();
  }
  static getRecruitLeadStatusMessages(user: any): { title: string; bg: string; text: string; date?: Date } {
    switch (user.Seeker?.RecruitLead?.Status) {
      case 4: //Hot
        return {
          title: 'تایید شده توسط کارشناس', bg: 'fade-green-bg',
          text: 'کارشناسان ما در حال جستجو برای پیدا کردن شغل مناسب شما هستن. در صورت پیدا شدن شغل مناسب با شما تماس می‌گیریم.',
          date: user.Seeker.RecruitLead.StaffConfirmDate
        };
      case 3: //Warm
        return user.Seeker?.RecruitLead?.SeekerConfirmDate ? {
          title: 'در انتظار تأیید کارشناس', bg: 'fade-yellow-bg',
          text: 'کارشناسان ما در حال بررسی اطلاعات پرونده شما هستند.',
          date: user.Seeker.RecruitLead.SeekerConfirmDate
        } : {
          title: 'درانتظار تایید اطلاعات', bg: 'fade-red-bg',
          text: 'اطلاعات شما تکمیل شده. وارد آخرین مرحله (قرمز رنگ) بشین و اطلاعاتتون رو بررسی و تایید کنین.',
          date: user.Seeker.RecruitLead.LukewarmDate
        };
      case 2: //Lukewarm
        return {
          title: 'درانتظار تکمیل اطلاعات', bg: 'fade-red-bg',
          text: 'برای پیدا کردن شغل مناسب لازمه شما رو بهتر بشناسیم. مراحل قرمز رنگ پایین رو باز و اطلاعاتتون رو وارد کنین.',
          date: user.Seeker.RecruitLead.LukewarmDate
        };
      default:
        return {
          title: 'درانتظار ثبت‌نام', bg: 'fade-red-bg',
          text: 'شما هنوز برای دریافت خدمات کاریابی ثبت نام نکردین‌. برای دریافت خدمات گزینه‌ی ثبت نام رو انتخاب و مراحل رو تکمیل کنین.',
          date: user?.Seeker?.RecruitLead?.LastUpdate
        };
    }
  }

	getVanguardsByDate$(criteria: any) {
		return this.http.post<any>(environment.app.apiUrl + `karpasha/vanguards-by-date`, criteria);
	}
}
